/** Универсальный reconnecting WebSocket-клиент с heartbeat.
 *
 *  Транспорт для всех WS-каналов приложения (уведомления, доска «Мониторинг»
 *  и т. д.). Подключается к `ws(s)://<host><path>`, передавая DRF-токен
 *  под-протоколом (`Sec-WebSocket-Protocol: bearer, <token>`), а НЕ в адресе —
 *  так токен не попадает в access-логи nginx/прокси (см.
 *  `apps/notifications/middleware.py`).
 *
 *  Что делает: авто-реконнект (экспоненциальный backoff), `ping` каждые 25s,
 *  реконнект при тишине дольше 60s. Разбор конкретных серверных событий —
 *  забота вызывающего кода (`onMessage` получает уже распарсенный JSON).
 */
export type WsState = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface RealtimeSocketOptions {
  /** Token из sessionStorage (DRF authtoken). */
  token: string;
  /** Базовый URL API — для построения ws:// из http://. Пустая строка = same-origin. */
  apiBaseUrl: string;
  /** WS-путь на сервере, напр. `/ws/notifications/` или `/ws/monitoring/`. */
  path: string;
  /** Распарсенное серверное сообщение (тип проверяет вызывающий код). */
  onMessage: (event: unknown) => void;
  onStateChange?: (state: WsState) => void;
}

const PING_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 60_000;
const RECONNECT_BACKOFF = [500, 1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

function buildWsUrl(apiBaseUrl: string, path: string): string {
  // Если apiBaseUrl пустой — same-origin: берём текущий host.
  let base = apiBaseUrl.trim();
  if (!base) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    base = `${proto}//${window.location.host}`;
  } else {
    base = base.replace(/^http:\/\//, "ws://").replace(/^https:\/\//, "wss://");
    base = base.replace(/\/$/, "");
  }
  // Токен уходит под-протоколом (см. connect()), а не в query — в адресе секрета нет.
  return `${base}${path}`;
}

export class RealtimeSocket {
  private ws: WebSocket | null = null;
  private state: WsState = "idle";
  private opts: RealtimeSocketOptions;
  private reconnectAttempt = 0;
  private pingTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private explicitClose = false;

  constructor(opts: RealtimeSocketOptions) {
    this.opts = opts;
  }

  getState(): WsState {
    return this.state;
  }

  connect(): void {
    if (this.state === "connecting" || this.state === "open") return;
    this.explicitClose = false;
    this.setState("connecting");
    try {
      const url = buildWsUrl(this.opts.apiBaseUrl, this.opts.path);
      // Токен — вторым под-протоколом после маркера 'bearer'
      // (Sec-WebSocket-Protocol: bearer, <token>). Не логируется как часть адреса.
      this.ws = new WebSocket(url, ["bearer", this.opts.token]);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.setState("open");
      this.startPing();
      this.resetHeartbeat();
    };
    this.ws.onmessage = (e) => {
      this.resetHeartbeat();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(String(e.data));
      } catch {
        return;
      }
      if (parsed) this.opts.onMessage(parsed);
    };
    this.ws.onerror = () => {
      // onclose всё равно будет вызван — реконнектимся там.
    };
    this.ws.onclose = () => {
      this.stopPing();
      this.stopHeartbeat();
      this.ws = null;
      if (this.explicitClose) {
        this.setState("closed");
        return;
      }
      this.scheduleReconnect();
    };
  }

  disconnect(): void {
    this.explicitClose = true;
    this.cancelReconnect();
    this.stopPing();
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close(1000, "client-disconnect");
      } catch {
        /* noop */
      }
      this.ws = null;
    }
    this.setState("closed");
  }

  send(payload: object): void {
    if (this.ws && this.state === "open") {
      try {
        this.ws.send(JSON.stringify(payload));
      } catch {
        /* noop */
      }
    }
  }

  private setState(s: WsState): void {
    if (s === this.state) return;
    this.state = s;
    this.opts.onStateChange?.(s);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ type: "ping" });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private resetHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setTimeout(() => {
      // Тишина дольше HEARTBEAT_TIMEOUT_MS — переподключаемся.
      try {
        this.ws?.close(4000, "heartbeat-timeout");
      } catch {
        /* noop */
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.cancelReconnect();
    this.setState("reconnecting");
    const idx = Math.min(this.reconnectAttempt, RECONNECT_BACKOFF.length - 1);
    const delay = RECONNECT_BACKOFF[idx];
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
